/**
 * 👍 / 👎 under a finished assistant reply.
 *
 * Low friction first: one click records the verdict. A thumbs-down then opens a
 * short, optional follow-up inline — quick reasons as chips and a comment —
 * but the negative signal is already saved before it appears, so abandoning it
 * still tells us the answer missed.
 *
 * The endpoint upserts per (user, message), so adding a reason afterwards
 * replaces the bare rating rather than double-counting, and a change of mind
 * simply overwrites.
 *
 * A failed save never interrupts the chat: no toast, no dialog — one muted line
 * with a retry, in place.
 */

import { Icon, RefreshCw, ThumbsDown, ThumbsUp } from "@norican/icons";
import { Button, Chip, ChipGroup, cn, Field, Textarea, Typography } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useT } from "@/i18n";
import type { MyFeedback } from "@/lib/api";
import { logger } from "@/lib/logger";

import { feedbackKeys, useSubmitFeedback } from "./api";
import {
  buildFeedbackBody,
  FEEDBACK_COMMENT_MAX,
  FEEDBACK_REASON_CODES,
  type FeedbackRatingValue,
  type FeedbackRequestBody,
  normaliseReasons,
} from "./feedback-draft";

export interface FeedbackControlsProps {
  /** The persisted assistant message being rated. */
  messageId: string;
  /** The reader's verdict as the server last reported it, if any. */
  initial?: MyFeedback | null;
  className?: string;
}

export function FeedbackControls({ messageId, initial = null, className }: FeedbackControlsProps) {
  const { t } = useT("assistant");
  const queryClient = useQueryClient();
  const submit = useSubmitFeedback();

  // What this tab last saved wins over the server copy: the transcript may
  // have been fetched before the click that changed it.
  const [saved] = useState<MyFeedback | null>(
    () => queryClient.getQueryData<MyFeedback>(feedbackKeys.verdict(messageId)) ?? initial,
  );
  const [rating, setRating] = useState<FeedbackRatingValue | null>(saved?.rating ?? null);
  const [reasons, setReasons] = useState<string[]>(saved?.reason_codes ?? []);
  const [comment, setComment] = useState(saved?.comment ?? "");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsSent, setDetailsSent] = useState(false);
  const [failedBody, setFailedBody] = useState<FeedbackRequestBody | null>(null);

  const send = (body: FeedbackRequestBody) => {
    setFailedBody(null);
    submit.mutate(body, {
      onError: (error) => {
        logger.warn("[feedback] could not save", body.target_id, error);
        setFailedBody(body);
      },
    });
  };

  const handleRate = (next: FeedbackRatingValue) => {
    setRating(next);
    setDetailsSent(false);
    setDetailsOpen(next === "NOT_HELPFUL");
    send(
      buildFeedbackBody(
        next === "NOT_HELPFUL"
          ? { messageId, rating: next, reasons, comment }
          : { messageId, rating: next },
      ),
    );
  };

  const handleSendDetails = () => {
    send(buildFeedbackBody({ messageId, rating: "NOT_HELPFUL", reasons, comment }));
    setDetailsOpen(false);
    setDetailsSent(true);
  };

  const prompt =
    rating === null
      ? t("feedback.question")
      : detailsSent
        ? t("feedback.thanks")
        : t("feedback.given");

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center gap-1">
        <Typography variant="caption" tone="muted" className="mr-1">
          {prompt}
        </Typography>

        <Button
          variant={rating === "HELPFUL" ? "subtle" : "ghost"}
          tone={rating === "HELPFUL" ? "success" : "neutral"}
          size="sm"
          iconOnly
          aria-label={t("feedback.helpful")}
          aria-pressed={rating === "HELPFUL"}
          onClick={() => handleRate("HELPFUL")}
        >
          <Icon icon={ThumbsUp} size="sm" />
        </Button>

        <Button
          variant={rating === "NOT_HELPFUL" ? "subtle" : "ghost"}
          tone={rating === "NOT_HELPFUL" ? "danger" : "neutral"}
          size="sm"
          iconOnly
          aria-label={t("feedback.notHelpful")}
          aria-pressed={rating === "NOT_HELPFUL"}
          aria-expanded={rating === "NOT_HELPFUL" ? detailsOpen : undefined}
          onClick={() => handleRate("NOT_HELPFUL")}
        >
          <Icon icon={ThumbsDown} size="sm" />
        </Button>

        {rating === "NOT_HELPFUL" && !detailsOpen ? (
          <Button variant="ghost" tone="neutral" size="sm" onClick={() => setDetailsOpen(true)}>
            {t("feedback.addDetails")}
          </Button>
        ) : null}

        {failedBody === null ? null : (
          <span className="flex items-center gap-1" role="status">
            <Typography variant="caption" tone="muted">
              {t("feedback.failed")}
            </Typography>
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={submit.isPending}
              onClick={() => send(failedBody)}
            >
              {t("feedback.retry")}
            </Button>
          </span>
        )}
      </div>

      {rating === "NOT_HELPFUL" && detailsOpen ? (
        <div className="border-border-subtle flex flex-col gap-3 rounded-md border p-3">
          <div className="flex flex-col gap-2">
            <Typography variant="body-sm" id={`feedback-reasons-${messageId}`}>
              {t("feedback.reasonLabel")}
            </Typography>
            <ChipGroup.Root
              multiple
              aria-labelledby={`feedback-reasons-${messageId}`}
              value={reasons}
              onValueChange={(value) => setReasons(normaliseReasons(value))}
            >
              {FEEDBACK_REASON_CODES.map((code) => (
                <Chip key={code} value={code} size="sm">
                  {t(`feedback.reason.${code}`)}
                </Chip>
              ))}
            </ChipGroup.Root>
          </div>

          <Field
            label={t("feedback.commentLabel")}
            hint={t("feedback.commentHint", { max: FEEDBACK_COMMENT_MAX })}
          >
            <Textarea
              rows={2}
              autoResize
              maxLength={FEEDBACK_COMMENT_MAX}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </Field>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" tone="neutral" size="sm" onClick={() => setDetailsOpen(false)}>
              {t("feedback.skip")}
            </Button>
            <Button size="sm" onClick={handleSendDetails}>
              {t("feedback.submit")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
